import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SocketService } from './socket.service';
import { authService } from './auth.service';

export interface TodayProgressStats {
  connected: {
    count: number;
    leads: LeadProgressSummary[];
  };
  scheduledAppointments: {
    count: number;
    appointments: AppointmentProgressSummary[];
  };
  completedAppointments: {
    count: number;
    appointments: AppointmentProgressSummary[];
  };
  enrollments: {
    count: number;
    leads: LeadProgressSummary[];
  };
  won: {
    count: number;
    leads: LeadProgressSummary[];
  };
}

export interface LeadProgressSummary {
  id: number;
  fullName: string | null;
  program?: string;
  campus?: string;
  changedAt?: Date;
}

export interface AppointmentProgressSummary {
  id: number;
  leadId?: number;
  leadName: string | null;
  scheduledStart: Date;
  status?: string;
  category?: string;
  createdAt?: Date;
  completedAt?: Date;
  // New fields for completed appointments
  attendanceStatus?: string;  // e.g., "Showed Up", "No Show", "Rescheduled"
  countsAsShow?: boolean;     // Whether this attendance status counts as showed up
  outcome?: string;           // e.g., "Enrolled", "Interested", "Not Interested"
}



export interface UserAction {
  id: number;
  actionType: string;
  priority: 'NOW' | 'TODAY' | 'LATER';
  status?: string;
  title: string;
  description?: string;
  entityType?: string;
  entityId?: number;
  metadata?: any;
  createdAt: Date | string;
  viewedAt?: Date;
  icon?: string;
  snoozedUntil?: Date | string;
  actionTaken?: string; // ADD THIS LINE
  lead?: {
    id: number;
    name: string;
    fullName: string;
    email: string;
    phone?: string;
    createdAt: string;
    last_activity_time?: string;
    // New fields
    step?: {
      id: number;
      name: string;
    };
    program?: {
      id: number;
      name: string;
    };
    campus?: {
      id: number;
      name: string;
    };
    source?: {
      id: number;
      name: string;
    };
    assignedRep?: {
      id: number;
      name: string;
      email: string;
    };
    secondaryRep?: {
      id: number;
      name: string;
      email: string;
    };
  };
  task?: {
    id: number;
    title: string;
    startDate: any;
    endDate: any;
    type?: any;
    // New: lead info for tasks
    lead?: {
      id: number;
      fullName: string;
      step?: {
        id: number;
        name: string;
      };
      campus?: {
        id: number;
        name: string;
      };
      program?: {
        id: number;
        name: string;
      };
    };
  };
  completedAt?: any;
  showMyAction?: boolean; // For My Actions modal filtering based on work queues
}

interface CursorPaginatedResponse<T> {
  data: T[];
  pagination: {
    limit: number;
    hasMore: boolean;
    nextCursor: number | null;
  };
}

export interface EngagementHubFilters {
  section?: 'new-leads' | 'follow-ups' | 'stalled-deals';
  priority?: 'NOW' | 'TODAY' | 'LATER';
  status?: string;
  limit?: number;
  cursor?: number;
}

export interface EngagementHubStats {
  sections: {
    'new-leads': {
      count: number;
      breakdown: { now: number; today: number; total: number };
    };
    'follow-ups': {
      count: number;
      breakdown: { now: number; today: number; total: number };
    };
    'stalled-deals': {
      count: number;
      breakdown: { now: number; today: number; total: number };
    };
  };
  total: { now: number; today: number; total: number };
}

type StatsMode = 'LOCAL_TRACKING' | 'API_ONLY';

@Injectable({
  providedIn: 'root'
})
export class UserActionService {
  // Keep the in-memory actions for real-time updates (when modal is open)
  private actionsSubject = new BehaviorSubject<UserAction[]>([]);
  public actions$ = this.actionsSubject.asObservable();

  // Signals for reactive counts
  private _nowCount = signal(0);
  private _todayCount = signal(0);
  private _totalCount = signal(0);

  // Track if we're managing in-memory state (when modal is open)
  private isManagingInMemoryState = false;

  // Track if listeners are already setup to prevent duplicates
  private listenersSetup = false;

  // ✨ STATS MODE CONFIGURATION ✨
  private _statsMode: StatsMode = 'LOCAL_TRACKING'; // Change this to switch modes
  private periodicSyncInterval: any;
  private lastApiSync = new Date();

  // Computed signal for todo status
  public todoStatus = computed(() => {
    const now = this._nowCount();
    const today = this._todayCount();
    if (now > 0) return 'NOW';
    if (today > 0) return 'TODAY';
    return 'CLEAR';
  });

  // Observable for new actions (for dialog animations)
  private newActionSubject = new Subject<UserAction>();
  public newAction$ = this.newActionSubject.asObservable();


  private actionUpdatedSubject = new Subject<{ actionId: number; updates: Partial<UserAction> }>();
  private actionRemovedSubject = new Subject<{ actionId: number; reason: 'completed' | 'deleted' | 'transferred' }>();
  private statsUpdatedSubject = new Subject<{ section?: string; type: 'increment' | 'decrement' | 'refresh' }>();

  // Public observables for components (add these to your existing service)
  public actionUpdated$ = this.actionUpdatedSubject.asObservable();
  public actionRemoved$ = this.actionRemovedSubject.asObservable();
  public statsUpdated$ = this.statsUpdatedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private authTokenService: authService
  ) {


    // Expose methods for easy switching
    (window as any).userActionService = this;
    (window as any).switchToLocalTracking = () => this.setStatsMode('LOCAL_TRACKING');
    (window as any).switchToApiOnly = () => this.setStatsMode('API_ONLY');
    (window as any).getCurrentStatsMode = () => this._statsMode;
    (window as any).forceStatsSync = () => this.syncStatsFromAPI();

    this.debugSocketConnection();
    this.initializeSocketListeners();
    this.initializeStats();

    this.authTokenService.authState$.subscribe(isAuthenticated => {

      if (isAuthenticated) {

        this.listenersSetup = false;
        setTimeout(() => {
          this.initializeSocketListeners();
          this.initializeStats();
        }, 1000);
      } else {
        this.stopPeriodicSync();
      }
    });
  }

  // ✨ STATS MODE MANAGEMENT ✨

  /**
   * Switch between LOCAL_TRACKING and API_ONLY modes
   */
  setStatsMode(mode: StatsMode) {


    const previousMode = this._statsMode;
    this._statsMode = mode;

    if (mode === 'LOCAL_TRACKING') {
      this.startLocalTracking();
    } else {
      this.stopLocalTracking();
    }

    // Refresh stats with new mode
    this.initializeStats();


  }

  getStatsMode(): StatsMode {
    return this._statsMode;
  }

  private startLocalTracking() {


    // Start periodic sync every 30 minutes
    this.startPeriodicSync();

    // Initialize local stats from API
    this.syncStatsFromAPI();
  }

  private stopLocalTracking() {


    // Stop periodic sync
    this.stopPeriodicSync();
  }

  private startPeriodicSync() {
    // Clear existing interval
    this.stopPeriodicSync();

    // Sync every 30 minutes (1800000 ms)
    this.periodicSyncInterval = setInterval(() => {

      this.syncStatsFromAPI();
    }, 30 * 60 * 1000);


  }

  private stopPeriodicSync() {
    if (this.periodicSyncInterval) {
      clearInterval(this.periodicSyncInterval);
      this.periodicSyncInterval = null;

    }
  }

  // ✨ STATS LOADING METHODS ✨

  /**
   * Initialize stats based on current mode
   */
  async initializeStats() {
    if (this._statsMode === 'LOCAL_TRACKING') {
      await this.syncStatsFromAPI();
      this.startPeriodicSync();
    } else {
      await this.loadActionCountsFromAPI();
    }
    // Also load My Actions filtered stats
    await this.loadMyActionsStats();
  }

  /**
   * Sync stats from API (for local tracking mode)
   */
  async syncStatsFromAPI() {
    try {

      const response = await this.http.get<{ now: number, today: number, total: number }>(
        `${environment.apiUrl}/actions/stats`
      ).toPromise();

      if (response) {
        this._nowCount.set(response.now);
        this._todayCount.set(response.today);
        this._totalCount.set(response.total);
        this.lastApiSync = new Date();

      }

      // Also refresh My Actions filtered stats
      await this.loadMyActionsStats();
    } catch (error) {
      console.error('❌ Error syncing stats from API:', error);
    }
  }

  /**
   * Load stats from API (for API-only mode)
   */
  async loadActionCountsFromAPI() {
    try {

      const response = await this.http.get<{ now: number, today: number, total: number }>(
        `${environment.apiUrl}/actions/stats`
      ).toPromise();

      if (response) {
        this._nowCount.set(response.now);
        this._todayCount.set(response.today);
        this._totalCount.set(response.total);

      }

      // Also refresh My Actions filtered stats
      await this.loadMyActionsStats();
    } catch (error) {
      console.error('❌ Error loading action counts:', error);
    }
  }

  // Load full actions list (when modal opens) - unchanged
  async loadFullActionsList() {
    try {
      this.isManagingInMemoryState = true;


      const response = await this.getActionsByPriorityCursor(null, null, 100);
      const actionsWithStatus = response.data.map(action => ({
        ...action,
        status: action.status || 'PENDING'
      }));

      this.actionsSubject.next(actionsWithStatus);
      this.updateInMemoryCounts(actionsWithStatus);
    } catch (error) {
      console.error('Error loading full actions list:', error);
      this.actionsSubject.next([]);
      this.updateInMemoryCounts([]);
    }
  }

  // Stop managing in-memory state (when modal closes)
  stopManagingInMemoryState() {
    this.isManagingInMemoryState = false;
    this.actionsSubject.next([]);

    // Refresh stats based on mode
    if (this._statsMode === 'LOCAL_TRACKING') {
      // Keep local stats (don't reload)

    } else {
      this.loadActionCountsFromAPI();
    }
  }

  private debugSocketConnection() {
    const socket = (this.socketService as any).socket;

    if (socket) {
      socket.removeAllListeners('connect');
      socket.removeAllListeners('disconnect');
      socket.removeAllListeners('error');

      socket.on('connect', () => {
        console.log('✅ Socket connected successfully');
      });

      socket.on('disconnect', () => {
        console.log('❌ Socket disconnected');
        this.listenersSetup = false;
      });

      socket.on('error', (error: any) => {
        console.error('🔥 Socket error:', error);
      });
    }
  }

  private initializeSocketListeners() {
    if (this.listenersSetup) {

      return;
    }

    const socket = (this.socketService as any).socket;

    if (socket && socket.connected) {
      this.authenticateAndSetupListeners();
    } else if (socket) {
      socket.removeAllListeners('connect');
      socket.on('connect', () => {

        this.authenticateAndSetupListeners();
      });
    } else {
      setTimeout(() => this.initializeSocketListeners(), 1000);
    }
  }

  private authenticateAndSetupListeners() {
    if (this.listenersSetup) {

      return;
    }


    this.socketService.authenticate();

    setTimeout(() => {
      this.setupListeners();
    }, 500);
  }

  private setupListeners() {
    if (this.listenersSetup) {

      return;
    }

    const socket = (this.socketService as any).socket;



    const events = [
      'user_action_created',
      'user_action_completed',
      'user_action_deleted',
      'user_actions_bulk_completed',
      'user_actions_priority_updated',
      'user_action_reactivated',
      'user_action_transferred_out',
      'user_action_transferred_in',
      'user_action_snoozed'
    ];

    events.forEach(event => {
      if (socket) {
        socket.removeAllListeners(event);
      }
    });



    // Listen for new actions
    socket.on('user_action_created', (data: any) => {

      const actionWithStatus = {
        ...data,
        status: data.status || 'PENDING'
      };
      this.newActionSubject.next(actionWithStatus);

      // Handle stats based on mode
      if (this._statsMode === 'LOCAL_TRACKING') {
        // Check if there's an oldPriority that's different from current priority
        if (data.oldPriority && data.oldPriority !== data.priority) {
          this.decrementLocalStats(data.oldPriority);
          // Also decrement My Actions stats if applicable
          if (data.showMyAction) {
            this.decrementMyActionsLocalStats(data.oldPriority);
          }
        }
        this.incrementLocalStats(data.priority);
        // Also increment My Actions stats if showMyAction is true
        if (data.showMyAction) {
          this.incrementMyActionsLocalStats(data.priority);
        }
        this.statsUpdatedSubject.next({ type: 'increment' });
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      }

      if (this.isManagingInMemoryState) {
        this.handleNewAction(actionWithStatus);
      }
    });

    socket.on('user_action_completed', (data: { actionId: number; priority?: string }) => {

      this.actionRemovedSubject.next({ actionId: data.actionId, reason: 'completed' }); // ← ADD THIS

      // Handle stats based on mode
      if (this._statsMode === 'LOCAL_TRACKING') {
        let priority = data.priority;
        if (!priority && this.isManagingInMemoryState) {
          const action = this.actionsSubject.value.find(a => a.id === data.actionId);
          priority = action?.priority;
        }

        if (priority) {
          this.decrementLocalStats(priority);
          this.statsUpdatedSubject.next({ type: 'decrement' }); // ← ADD THIS
        } else {
          console.warn('⚠️ Priority unknown for completed action, syncing from API');
          this.syncStatsFromAPI();
          this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS
        }
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS
      }

      if (this.isManagingInMemoryState) {
        this.removeAction(data.actionId);
      }
    });

    socket.on('user_action_deleted', (data: { actionId: number; priority?: string }) => {

      this.actionRemovedSubject.next({ actionId: data.actionId, reason: 'deleted' }); // ← ADD THIS

      // Handle stats based on mode
      if (this._statsMode === 'LOCAL_TRACKING') {
        let priority = data.priority;
        if (!priority && this.isManagingInMemoryState) {
          const action = this.actionsSubject.value.find(a => a.id === data.actionId);
          priority = action?.priority;
        }

        if (priority) {
          this.decrementLocalStats(priority);
          this.statsUpdatedSubject.next({ type: 'decrement' }); // ← ADD THIS
        } else {
          console.warn('⚠️ Priority unknown for deleted action, syncing from API');
          this.syncStatsFromAPI();
          this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS
        }
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS
      }

      if (this.isManagingInMemoryState) {
        this.removeAction(data.actionId);
      }
    });

    socket.on('user_actions_bulk_completed', (data: {
      actionIds: number[];
      completedCount: number;
      priorityCounts?: { NOW?: number; TODAY?: number; LATER?: number };
    }) => {

      // ← ADD THIS block
      data.actionIds.forEach(actionId => {
        this.actionRemovedSubject.next({ actionId, reason: 'completed' });
      });

      if (this._statsMode === 'LOCAL_TRACKING') {
        try {
          if (!data || !Array.isArray(data.actionIds)) {
            console.error('Invalid bulk completion data received:', data);
            return;
          }

          if (data.priorityCounts) {
            if (data.priorityCounts.NOW) {
              this.decrementLocalStatsByCount('NOW', data.priorityCounts.NOW);
            }
            if (data.priorityCounts.TODAY) {
              this.decrementLocalStatsByCount('TODAY', data.priorityCounts.TODAY);
            }
            if (data.priorityCounts.LATER) {
              this.decrementLocalStatsByCount('LATER', data.priorityCounts.LATER);
            }
            this.statsUpdatedSubject.next({ type: 'decrement' }); // ← ADD THIS
          } else if (this.isManagingInMemoryState) {
            // ... existing logic ...
            this.statsUpdatedSubject.next({ type: 'decrement' }); // ← ADD THIS
          } else {
            console.warn('⚠️ No priority info for bulk completion, syncing from API');
            this.syncStatsFromAPI();
            this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS
          }

          // ... rest of existing logic ...
        } catch (error) {
          console.error('Error handling bulk action completion:', error);
          if (this._statsMode === 'LOCAL_TRACKING') {
            this.syncStatsFromAPI();
            this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS
          }
        }
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' }); // ← ADD THIS

        if (this.isManagingInMemoryState) {
          data.actionIds.forEach(actionId => {
            if (typeof actionId === 'number') {
              this.removeAction(actionId);
            }
          });
        }
      }
    });

    // Add this to your setupListeners() method in UserActionService

    socket.on('user_action_snoozed', (data: {
      actionId: number;
      snoozedUntil: Date | string;
      priority: string;
      oldPriority?: string;
    }) => {

      // Handle stats based on mode
      if (this._statsMode === 'LOCAL_TRACKING') {
        // Check if priority changed during snooze
        if (data.oldPriority && data.oldPriority !== data.priority) {
          this.decrementLocalStats(data.oldPriority);
          this.incrementLocalStats(data.priority);
          this.statsUpdatedSubject.next({ type: 'refresh' });
        }
        // If no priority change, stats don't change (snoozed actions still count in totals)
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      }

      // Update in-memory list if modal is open
      if (this.isManagingInMemoryState) {
        const current = this.actionsSubject.value;
        const updated = current.map(a =>
          a.id === data.actionId ? {
            ...a,
            status: 'SNOOZED',
            priority: data.priority as any,
            snoozedUntil: data.snoozedUntil
          } : a
        );
        this.actionsSubject.next(updated);
        this.updateInMemoryCounts(updated);
      }

      this.actionUpdatedSubject.next({
        actionId: data.actionId,
        updates: {
          status: 'SNOOZED',
          snoozedUntil: data.snoozedUntil,
          priority: data.priority as any
        }
      });
    });

    socket.on('user_actions_priority_updated', (data: {
      actionIds: number[];
      newPriority: string;
      oldPriority?: string;
    }) => {

      if (this._statsMode === 'LOCAL_TRACKING') {
        if (data.oldPriority && data.oldPriority !== data.newPriority) {
          const count = data.actionIds.length;
          this.decrementLocalStatsByCount(data.oldPriority as any, count);
          this.incrementLocalStatsByCount(data.newPriority as any, count);
          this.statsUpdatedSubject.next({ type: 'refresh' });
          // this.loadActionCountsFromAPI();
        } else if (this.isManagingInMemoryState) {
          const currentActions = this.actionsSubject.value;
          const priorityChanges = { NOW: 0, TODAY: 0, LATER: 0 };

          data.actionIds.forEach(actionId => {
            const action = currentActions.find(a => a.id === actionId);
            if (action && action.priority !== data.newPriority) {
              priorityChanges[action.priority]--;
              priorityChanges[data.newPriority as keyof typeof priorityChanges]++;
            }
          });

          this.adjustLocalStatsByDeltas(priorityChanges);
          this.statsUpdatedSubject.next({ type: 'refresh' });
        }
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      }

      if (this.isManagingInMemoryState) {
        this.updateActionPriorities(data.actionIds, data.newPriority);
      }

      data.actionIds.forEach(actionId => {
        this.actionUpdatedSubject.next({
          actionId,
          updates: { priority: data.newPriority as any }
        });
      });
    });

    socket.on('user_action_reactivated', (data: any) => {


      const actionWithStatus = {
        ...data,
        status: data.status || 'PENDING'
      };
      this.newActionSubject.next(actionWithStatus);

      if (this._statsMode === 'LOCAL_TRACKING') {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      }

      if (this.isManagingInMemoryState) {
        this.handleNewAction(actionWithStatus);
      }
    });

    socket.on('user_action_transferred_out', (data: {
      actionId: number;
      actionType: string;
      leadId: number;
      transferredTo: number;
      reason: string;
      priority?: string;
    }) => {


      if (this._statsMode === 'LOCAL_TRACKING') {
        let priority = data.priority;
        if (!priority && this.isManagingInMemoryState) {
          const action = this.actionsSubject.value.find(a => a.id === data.actionId);
          priority = action?.priority;
        }

        if (priority) {
          this.decrementLocalStats(priority);
          this.statsUpdatedSubject.next({ type: 'refresh' });
        } else {
          console.warn('⚠️ Priority unknown for transferred out action, syncing from API');
          this.syncStatsFromAPI();
          this.statsUpdatedSubject.next({ type: 'refresh' });
        }
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      }

      if (this.isManagingInMemoryState) {
        this.removeAction(data.actionId);
      }

      this.actionRemovedSubject.next({ actionId: data.actionId, reason: 'transferred' });

    });

    socket.on('user_action_transferred_in', (data: UserAction) => {


      const actionWithStatus = {
        ...data,
        status: data.status || 'PENDING'
      };
      this.newActionSubject.next(actionWithStatus);

      if (this._statsMode === 'LOCAL_TRACKING') {
        this.incrementLocalStats(data.priority);
        this.statsUpdatedSubject.next({ type: 'refresh' });
      } else {
        this.loadActionCountsFromAPI();
        this.statsUpdatedSubject.next({ type: 'refresh' });
      }

      if (this.isManagingInMemoryState) {
        this.handleNewAction(actionWithStatus);
      }
    });

    this.listenersSetup = true;
  }

  // ✨ LOCAL STATS CALCULATION METHODS ✨

  private incrementLocalStats(priority: string) {
    if (this._statsMode !== 'LOCAL_TRACKING') return;

    if (priority === 'NOW') {
      this._nowCount.set(this._nowCount() + 1);
    } else if (priority === 'TODAY') {
      this._todayCount.set(this._todayCount() + 1);
    }
    this._totalCount.set(this._totalCount() + 1);
  }

  private decrementLocalStats(priority: string) {
    if (this._statsMode !== 'LOCAL_TRACKING') return;



    if (priority === 'NOW') {
      this._nowCount.set(Math.max(0, this._nowCount() - 1));
    } else if (priority === 'TODAY') {
      this._todayCount.set(Math.max(0, this._todayCount() - 1));
    }
    this._totalCount.set(Math.max(0, this._totalCount() - 1));


  }

  private incrementLocalStatsByCount(priority: string, count: number) {
    if (this._statsMode !== 'LOCAL_TRACKING' || count <= 0) return;



    if (priority === 'NOW') {
      this._nowCount.set(this._nowCount() + count);
    } else if (priority === 'TODAY') {
      this._todayCount.set(this._todayCount() + count);
    }
    this._totalCount.set(this._totalCount() + count);
  }

  private decrementLocalStatsByCount(priority: string, count: number) {
    if (this._statsMode !== 'LOCAL_TRACKING' || count <= 0) return;



    if (priority === 'NOW') {
      this._nowCount.set(Math.max(0, this._nowCount() - count));
    } else if (priority === 'TODAY') {
      this._todayCount.set(Math.max(0, this._todayCount() - count));
    }
    this._totalCount.set(Math.max(0, this._totalCount() - count));
  }

  private adjustLocalStatsByDeltas(deltas: { NOW: number; TODAY: number; LATER: number }) {
    if (this._statsMode !== 'LOCAL_TRACKING') return;



    this._nowCount.set(Math.max(0, this._nowCount() + deltas.NOW));
    this._todayCount.set(Math.max(0, this._todayCount() + deltas.TODAY));
    const totalDelta = deltas.NOW + deltas.TODAY + deltas.LATER;
    this._totalCount.set(Math.max(0, this._totalCount() + totalDelta));
  }

  // My Actions filtered stats local tracking methods
  private incrementMyActionsLocalStats(priority: string) {
    if (this._statsMode !== 'LOCAL_TRACKING') return;

    if (priority === 'NOW') {
      this._myActionsNowCount.set(this._myActionsNowCount() + 1);
    } else if (priority === 'TODAY') {
      this._myActionsTodayCount.set(this._myActionsTodayCount() + 1);
    }
    this._myActionsTotalCount.set(this._myActionsTotalCount() + 1);
  }

  private decrementMyActionsLocalStats(priority: string) {
    if (this._statsMode !== 'LOCAL_TRACKING') return;

    if (priority === 'NOW') {
      this._myActionsNowCount.set(Math.max(0, this._myActionsNowCount() - 1));
    } else if (priority === 'TODAY') {
      this._myActionsTodayCount.set(Math.max(0, this._myActionsTodayCount() - 1));
    }
    this._myActionsTotalCount.set(Math.max(0, this._myActionsTotalCount() - 1));
  }

  // In-memory state management methods (unchanged)
  private handleNewAction(action: UserAction) {
    if (!this.isManagingInMemoryState) return;


    const current = this.actionsSubject.value;

    const exists = current.some(a => a.id === action.id);
    if (exists) {

      const updated = current.map(a => a.id === action.id ? action : a);
      this.actionsSubject.next(updated);
    } else {
      const updated = [action, ...current];
      this.actionsSubject.next(updated);
    }
  }

  private removeAction(actionId: number) {
    if (!this.isManagingInMemoryState) return;


    const current = this.actionsSubject.value;
    const filtered = current.filter(a => a.id !== actionId);
    this.actionsSubject.next(filtered);
  }

  private updateActionPriorities(actionIds: number[], newPriority: string) {
    if (!this.isManagingInMemoryState) return;

    const current = this.actionsSubject.value;
    const updated = current.map(a =>
      actionIds.includes(a.id) ? { ...a, priority: newPriority as any } : a
    );
    this.actionsSubject.next(updated);
  }

  private updateInMemoryCounts(actions: UserAction[]) {
    if (!this.isManagingInMemoryState) return;



    const now = new Date();
    const pendingActions = actions.filter(a => {
      if (!a.status || a.status === 'PENDING') return true;

      if (a.status === 'SNOOZED' && a.snoozedUntil) {
        const snoozedUntil = new Date(a.snoozedUntil);
        return snoozedUntil > now;
      }

      return false;
    });

    const nowCount = pendingActions.filter(a => a.priority === 'NOW').length;
    const todayCount = pendingActions.filter(a => a.priority === 'TODAY').length;
    const totalCount = pendingActions.length;

    // Only update if managing in-memory state (don't override local/API stats)
    if (this._statsMode === 'API_ONLY' || !this.listenersSetup) {
      this._nowCount.set(nowCount);
      this._todayCount.set(todayCount);
      this._totalCount.set(totalCount);
    }
  }

  // API methods (updated to respect mode)
  async getActionsByPriorityCursor(
    priority: string | null,
    cursor: number | null = null,
    limit: number = 20,
    section?: 'new-leads' | 'follow-ups' | 'stalled-deals'
  ): Promise<CursorPaginatedResponse<UserAction>> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString()
      });

      if (priority) {
        params.append('priority', priority);
      }

      if (cursor) {
        params.append('cursor', cursor.toString());
      }

      if (section) {
        params.append('section', section);
      }

      const response = await this.http.get<CursorPaginatedResponse<UserAction>>(
        `${environment.apiUrl}/actions/v2?${params.toString()}`
      ).toPromise();

      return response || { data: [], pagination: { limit, hasMore: false, nextCursor: null } };
    } catch (error) {
      console.error(`Error loading actions:`, error);
      return { data: [], pagination: { limit, hasMore: false, nextCursor: null } };
    }
  }

  async completeAction(actionId: number, actionTaken: string) {
    try {
      await this.http.post(`${environment.apiUrl}/actions/${actionId}/complete`, { actionTaken }).toPromise();

      // Don't update stats here - wait for socket event
      if (this.isManagingInMemoryState) {
        this.removeAction(actionId);
      }
    } catch (error) {
      console.error('Error completing action:', error);
      throw error;
    }
  }

  async snoozeAction(actionId: number, until: Date, priority?: 'NOW' | 'TODAY' | 'LATER') {
    try {
      await this.http.post(`${environment.apiUrl}/actions/${actionId}/snooze`, {
        until,
        priority
      }).toPromise();

      // Don't update stats here - wait for socket event if needed
      if (this.isManagingInMemoryState) {
        const current = this.actionsSubject.value;
        const updated = current.map(a =>
          a.id === actionId ? { ...a, status: 'SNOOZED', priority: priority || a.priority, snoozedUntil: until } : a
        );
        this.actionsSubject.next(updated);
      }
    } catch (error) {
      console.error('Error snoozing action:', error);
      throw error;
    }
  }

  async markViewed(actionId: number) {
    return this.http.put(`${environment.apiUrl}/actions/${actionId}/view`, {}).toPromise();
  }

  async updateActionPriority(actionId: number, priority: 'NOW' | 'TODAY' | 'LATER') {
    try {
      await this.http.patch(`${environment.apiUrl}/actions/${actionId}/priority`, { priority }).toPromise();

      // Don't update stats here - wait for socket event
      if (this.isManagingInMemoryState) {
        const current = this.actionsSubject.value;
        const updated = current.map(a =>
          a.id === actionId ? { ...a, priority } : a
        );
        this.actionsSubject.next(updated);
      }
    } catch (error) {
      console.error('Error updating action priority:', error);
      throw error;
    }
  }

  async reactivateAction(actionId: number) {
    try {
      await this.http.post(`${environment.apiUrl}/actions/${actionId}/reactivate`, {}).toPromise();

      // Don't update stats here - wait for socket event
      if (this.isManagingInMemoryState) {
        const current = this.actionsSubject.value;
        const updated = current.map(a =>
          a.id === actionId ? { ...a, status: 'PENDING', snoozedUntil: null } : a
        );
        this.actionsSubject.next(updated);
      }
    } catch (error) {
      console.error('Error reactivating action:', error);
      throw error;
    }
  }

  // Getter methods for counts (unfiltered - for Engagement Hub)
  getNowCount() { return this._nowCount(); }
  getTodayCount() { return this._todayCount(); }
  getTotalCount() { return this._totalCount(); }

  // Signals for My Actions filtered counts
  private _myActionsNowCount = signal(0);
  private _myActionsTodayCount = signal(0);
  private _myActionsTotalCount = signal(0);

  // Getter methods for My Actions filtered counts
  getMyActionsNowCount() { return this._myActionsNowCount(); }
  getMyActionsTodayCount() { return this._myActionsTodayCount(); }
  getMyActionsTotalCount() { return this._myActionsTotalCount(); }

  // Computed signal for My Actions todo status
  public myActionsTodoStatus = computed(() => {
    const now = this._myActionsNowCount();
    const today = this._myActionsTodayCount();
    if (now > 0) return 'NOW';
    if (today > 0) return 'TODAY';
    return 'CLEAR';
  });

  /**
   * Load My Actions filtered stats from API
   */
  async loadMyActionsStats() {
    try {
      const response = await this.http.get<{ now: number, today: number, total: number }>(
        `${environment.apiUrl}/actions/stats/my-actions`
      ).toPromise();

      if (response) {
        this._myActionsNowCount.set(response.now);
        this._myActionsTodayCount.set(response.today);
        this._myActionsTotalCount.set(response.total);
      }
    } catch (error) {
      console.error('Error loading My Actions stats:', error);
    }
  }

  // Observable method for in-memory actions (used by modal)
  getActionsByPriority(priority: string): Observable<UserAction[]> {
    return this.actions$.pipe(
      map(actions => actions.filter(a =>
        a.priority === priority &&
        (!a.status || a.status === 'PENDING')
      ))
    );
  }

  // Check if managing in-memory state
  isManagingActions(): boolean {
    return this.isManagingInMemoryState;
  }

  // Debug methods
  checkListenerCount() {
    const socket = (this.socketService as any).socket;
    if (socket) {
      const listenerCount = socket.listeners('user_action_created').length;
      return listenerCount;
    }
    return 0;
  }

  getStatsInfo() {
    return {
      mode: this._statsMode,
      lastSync: this.lastApiSync,
      periodicSyncActive: !!this.periodicSyncInterval,
      counts: {
        now: this._nowCount(),
        today: this._todayCount(),
        total: this._totalCount()
      }
    };
  }


  /**
 * Get actions filtered by engagement hub section
 */
  async getActionsBySection(
    section: 'new-leads' | 'follow-ups' | 'stalled-deals',
    cursor?: number,
    limit: number = 50
  ): Promise<CursorPaginatedResponse<UserAction>> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString()
      });

      if (cursor) {
        params.append('cursor', cursor.toString());
      }

      const response = await this.http.get<CursorPaginatedResponse<UserAction>>(
        `${environment.apiUrl}/actions/section/${section}?${params.toString()}`
      ).toPromise();

      return response || { data: [], pagination: { limit, hasMore: false, nextCursor: null } };
    } catch (error) {
      console.error(`Error loading ${section} actions:`, error);
      return { data: [], pagination: { limit, hasMore: false, nextCursor: null } };
    }
  }

  /**
   * Get comprehensive engagement hub statistics
   */
  async getEngagementHubStats(): Promise<EngagementHubStats> {
    try {
      const response = await this.http.get<EngagementHubStats>(
        `${environment.apiUrl}/actions/engagement-hub/stats`
      ).toPromise();

      return response || {
        sections: {
          'new-leads': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'follow-ups': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'stalled-deals': { count: 0, breakdown: { now: 0, today: 0, total: 0 } }
        },
        total: { now: 0, today: 0, total: 0 }
      };
    } catch (error) {
      console.error('Error loading engagement hub stats:', error);
      return {
        sections: {
          'new-leads': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'follow-ups': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'stalled-deals': { count: 0, breakdown: { now: 0, today: 0, total: 0 } }
        },
        total: { now: 0, today: 0, total: 0 }
      };
    }
  }

  /**
   * Get stats for a specific section
   */
  async getSectionStats(section: 'new-leads' | 'follow-ups' | 'stalled-deals'): Promise<{ now: number, today: number, total: number }> {
    try {
      const response = await this.http.get<{ now: number, today: number, total: number }>(
        `${environment.apiUrl}/actions/stats?section=${section}`
      ).toPromise();

      return response || { now: 0, today: 0, total: 0 };
    } catch (error) {
      console.error(`Error loading ${section} stats:`, error);
      return { now: 0, today: 0, total: 0 };
    }
  }


  /**
   * Get actions using Prisma queries (V2)
   */
  async getActionsBySectionV2(
    section: 'new-leads' | 'follow-ups' | 'stalled-deals',
    cursor?: number | null,
    limit: number = 50,
    filterUserId?: number | 'all',
    stepIds?: number[],
    folderIds?: number[],
    subFolderIds?: number[]
  ): Promise<CursorPaginatedResponse<UserAction>> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString()
      });

      if (cursor) {
        params.append('cursor', cursor.toString());
      }

      if (filterUserId) {
        params.append('userId', filterUserId.toString());
      }

      if (stepIds && stepIds.length > 0) {
        params.append('stepIds', stepIds.join(','));
      }

      if (folderIds && folderIds.length > 0) {
        params.append('folderIds', folderIds.join(','));
      }

      if (subFolderIds && subFolderIds.length > 0) {
        params.append('subFolderIds', subFolderIds.join(','));
      }

      const response = await this.http.get<CursorPaginatedResponse<UserAction>>(
        `${environment.apiUrl}/actions/v2/section/${section}?${params.toString()}`
      ).toPromise();



      return response || { data: [], pagination: { limit, hasMore: false, nextCursor: null } };
    } catch (error) {
      console.error(`❌ Error loading ${section} actions (V2):`, error);
      return { data: [], pagination: { limit, hasMore: false, nextCursor: null } };
    }
  }


  async getEngagementHubStatsV2(
    filterUserId?: number | 'all',
    stepIds?: number[],
    folderIds?: number[],
    subFolderIds?: number[]
  ): Promise<EngagementHubStats> {
    try {
      const params: any = {};
      if (filterUserId) {
        params.userId = filterUserId.toString();
      }
      if (stepIds && stepIds.length > 0) {
        params.stepIds = stepIds.join(',');
      }
      if (folderIds && folderIds.length > 0) {
        params.folderIds = folderIds.join(',');
      }
      if (subFolderIds && subFolderIds.length > 0) {
        params.subFolderIds = subFolderIds.join(',');
      }

      const response = await this.http.get<EngagementHubStats>(
        `${environment.apiUrl}/actions/v2/engagement-hub/stats`,
        { params }
      ).toPromise();



      return response || {
        sections: {
          'new-leads': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'follow-ups': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'stalled-deals': { count: 0, breakdown: { now: 0, today: 0, total: 0 } }
        },
        total: { now: 0, today: 0, total: 0 }
      };
    } catch (error) {
      console.error('❌ Error loading engagement hub stats (V2):', error);
      return {
        sections: {
          'new-leads': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'follow-ups': { count: 0, breakdown: { now: 0, today: 0, total: 0 } },
          'stalled-deals': { count: 0, breakdown: { now: 0, today: 0, total: 0 } }
        },
        total: { now: 0, today: 0, total: 0 }
      };
    }
  }

  /**
   * Get section stats (V2)
   */
  async getSectionStatsV2(section: 'new-leads' | 'follow-ups' | 'stalled-deals', filterUserId?: number): Promise<{ now: number, today: number, total: number }> {
    try {
      const params = new URLSearchParams({
        section: section
      });

      if (filterUserId) {
        params.append('userId', filterUserId.toString());
      }

      const response = await this.http.get<{ now: number, today: number, total: number }>(
        `${environment.apiUrl}/actions/v2/stats?${params.toString()}`
      ).toPromise();



      return response || { now: 0, today: 0, total: 0 };
    } catch (error) {
      console.error(`❌ Error loading ${section} stats (V2):`, error);
      return { now: 0, today: 0, total: 0 };
    }
  }

  // Add these new methods to your frontend UserActionService

  async getCompletedTodayCount(filterUserId?: number | 'all', stepIds?: number[]): Promise<{ completed: number; total: number }> {
    try {
      const params: any = {};
      if (filterUserId) {
        params.userId = filterUserId.toString();
      }
      if (stepIds && stepIds.length > 0) {
        params.stepIds = stepIds.join(',');
      }

      const response = await this.http.get<{ completed: number; total: number }>(
        `${environment.apiUrl}/actions/completed/today/count`,
        { params }
      ).toPromise();

      return response || { completed: 0, total: 0 };
    } catch (error) {
      console.error('Error loading completed count:', error);
      return { completed: 0, total: 0 };
    }
  }


async getTodayProgressStats(filterUserId?: number | 'all' | null, stepIds?: number[]): Promise<TodayProgressStats> {
  try {
    const params: any = {};
    
    // Only pass userId if it's a specific user (not null/undefined)
    // Admin without userId filter will see all data
    if (filterUserId !== null && filterUserId !== undefined) {
      params.userId = filterUserId.toString();
    }
    
    if (stepIds && stepIds.length > 0) {
      params.stepIds = stepIds.join(',');
    }

    const response = await this.http.get<TodayProgressStats>(
      `${environment.apiUrl}/actions/today-progress`,
      { params }
    ).toPromise();

    return response || this.getEmptyProgressStats();
  } catch (error) {
    console.error('Error loading today progress stats:', error);
    return this.getEmptyProgressStats();
  }
}

private getEmptyProgressStats(): TodayProgressStats {
  return {
    connected: { count: 0, leads: [] },
    scheduledAppointments: { count: 0, appointments: [] },
    completedAppointments: { count: 0, appointments: [] },
    enrollments: { count: 0, leads: [] },
    won: { count: 0, leads: [] },
  };
}

/**
 * Get user's show_all_fresh_leads preference
 */
async getShowAllFreshLeadsPreference(): Promise<boolean> {
  try {
    const response = await this.http.get<{ showAllFreshLeads: boolean }>(
      `${environment.apiUrl}/actions/preferences/show-all-fresh-leads`
    ).toPromise();
    return response?.showAllFreshLeads ?? true;
  } catch (error) {
    console.error('Error getting show_all_fresh_leads preference:', error);
    return true; // Default to true
  }
}

/**
 * Update user's show_all_fresh_leads preference
 */
async updateShowAllFreshLeadsPreference(showAllFreshLeads: boolean): Promise<boolean> {
  try {
    const response = await this.http.patch<{ success: boolean; showAllFreshLeads: boolean }>(
      `${environment.apiUrl}/actions/preferences/show-all-fresh-leads`,
      { showAllFreshLeads }
    ).toPromise();
    return response?.showAllFreshLeads ?? showAllFreshLeads;
  } catch (error) {
    console.error('Error updating show_all_fresh_leads preference:', error);
    throw error;
  }
}

}